import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AOS from "aos";
import "aos/dist/aos.css";
import { useGstDiscount } from "../context/GstDiscountContext";

import { QrCode } from "./QrCode";
import api from "../utils/api";
import { globalAppCache } from "../utils/cache";
import { Minus, Plus, X, Printer, ArrowRightLeft } from "lucide-react";
import { formatIndianCurrency } from "../utils/Const";

const BillingDetails = ({
  billingLoading,
  billingDetails,
  handleBillingChange,
  orderItems,
  calculateTotal,
  generateBillSlip,
  removeFromOrder,
  onCancelOrder,
  addToOrderincrease,
  addToOrderdecrease,
  updateItemQty,
  updateItemNote,
  darkMode,
  happyHour,
  paymentMethod,
  setPaymentMethod,
  orderId,
  onPrintKOT,
  onTransferTable,
  orderInfo,
}) => {
  const { discount, gst, setDiscount, setGST, discountType, setDiscountType, fixedDiscountAmount, setFixedDiscountAmount } = useGstDiscount();
  const [userId, setUserId] = useState("");

  // const [paymentMethod, setPaymentMethod] = useState("COD");
  const [upiDetails, setUpiDetails] = useState(null);
  const [splitCash, setSplitCash] = useState("");
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [checkingCustomer, setCheckingCustomer] = useState(false);

  // 🔥 Loyalty & Wallet state
  const [customerProfile, setCustomerProfile] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [merchantSettings, setMerchantSettings] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      if (decodedToken.user) {
        setUserId(decodedToken.user.id);
      }
    }
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });
  }, []);

  useEffect(() => {
    const mobile = billingDetails.mobile;

    if (!mobile || mobile.length !== 10) {
      setIsExistingCustomer(false);
      return;
    }

    const fetchCustomer = async () => {
      try {
        setCheckingCustomer(true);

        const response = await api.get(`/customers/check/${mobile}`);

        if (response.data.exists) {
          setIsExistingCustomer(true);
          const cust = response.data.customer;
          setCustomerProfile(cust);

          handleBillingChange({
            target: {
              name: "name",
              value: cust.name,
            },
          });

          // Removed fetch merchant settings from here since it's now global

          console.log("Existing customer found");
        } else {
          setIsExistingCustomer(false);
          setCustomerProfile(null);
          setRedeemPoints(false);
          setUseWallet(false);
        }
      } catch (error) {
        const message =
          error.response?.data?.message || "Customer search failed";
        toast.error(message);
      } finally {
        setCheckingCustomer(false);
      }
    };

    fetchCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingDetails.mobile]);

  useEffect(() => {
    if (paymentMethod === "UPI") {
      api
        .get(`user/${userId}`)
        .then((response) => {
          setUpiDetails(response.data.upiId);
        })
        .catch((error) => {
          console.error("Error fetching UPI details:", error);
        });
    }
  }, [paymentMethod, userId]);

  useEffect(() => {
    if (!orderItems || orderItems.length === 0) {
      setDiscount(0);
      setGST(0);
      setFixedDiscountAmount(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems]);

  useEffect(() => {
    if (userId) {
      if (globalAppCache.userCache[userId]) {
        setMerchantSettings(globalAppCache.userCache[userId]);
      }
      
      api.get(`user/${userId}`)
        .then((mRes) => {
          globalAppCache.userCache[userId] = mRes.data;
          setMerchantSettings(mRes.data);
        })
        .catch((err) => console.error("Failed to fetch merchant settings", err));
    }
  }, [userId]);

  const calcBreakdown = () => {
    const totalAmount = calculateTotal();
    const discountAmount = discountType === "fixed"
      ? Math.min(fixedDiscountAmount || 0, totalAmount)
      : (totalAmount * discount) / 100;
    const afterDiscount = totalAmount - discountAmount;
    const gstAmount = (afterDiscount * gst) / 100;
    const preFee = afterDiscount + gstAmount;
    const platformFeeAmount = (merchantSettings?.platformFee?.enabled && merchantSettings.platformFee.percentage > 0)
      ? Math.round(preFee * merchantSettings.platformFee.percentage / 100 * 100) / 100
      : 0;
    return { discountAmount, gstAmount, preFee, platformFeeAmount, total: preFee + platformFeeAmount };
  };

  const calculateFinalAmount = () => calcBreakdown().total;

  return (
    <>
      {" "}
      <ToastContainer />
      <div
        className={`bill flex flex-col w-full h-full px-3 pt-4 pb-6 rounded-2xl border shadow-sm transition-colors duration-300 ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-slate-100"}`}
      >
        <div className="flex items-center justify-between mb-4">
          <p className={`text-base font-bold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>
            Billing Details
          </p>
          {onTransferTable && orderId && orderInfo?.orderType !== "DIRECT" && (
            <button
              onClick={onTransferTable}
              className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2.5 py-1.5 rounded-lg transition-colors"
            >
              <ArrowRightLeft size={11} />
              Transfer Table
            </button>
          )}
        </div>
        <div className="flex flex-col space-y-3 mb-4">
          <div className="flex items-center">
            <label className="w-1/4 text-slate-600 font-medium text-sm text-right pr-4 xl:block hidden">
              Mobile:
            </label>
            <input
              type="tel"
              name="mobile"
              placeholder="Enter Mobile No..."
              value={billingDetails.mobile}
              onChange={(e) => {
                let value = e.target.value;

                value = value.replace(/\D/g, "");

                if (value.length > 10) {
                  value = value.slice(-10);
                }

                handleBillingChange({
                  target: { name: "mobile", value },
                });
              }}
              className="bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition duration-150 focus:outline-none flex-grow"
            />
          </div>
          {checkingCustomer && (
            <p className="text-[11px] text-center text-slate-400 font-semibold mt-1">
              Checking customer...
            </p>
          )}

          {isExistingCustomer && (
            <p className="text-[11px] text-center text-emerald-600 font-bold mt-1">
              Existing customer detected
            </p>
          )}
          <div className="flex items-center">
            <label className="w-1/4 text-slate-600 font-medium text-sm text-right pr-4 xl:block hidden">
              Name:
            </label>

            <input
              type="text"
              name="name"
              placeholder="Enter name..."
              value={billingDetails.name}
              onChange={handleBillingChange}
              disabled={isExistingCustomer}
              className={`text-sm rounded-xl px-3 py-1.5 flex-grow focus:outline-none transition duration-150 ${
                isExistingCustomer
                  ? "bg-slate-100 border border-slate-200 text-slate-500 cursor-not-allowed font-medium"
                  : "bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              }`}
            />
          </div>
        </div>
        <p className={`text-base font-bold mb-3 ${darkMode ? "text-slate-100" : "text-slate-800"}`}>
          Order Summary
        </p>
        <div className="overflow-x-auto overflow-auto max-h-[500px] example">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className={`py-1 px-3 ${darkMode ? "bg-gray-700 text-slate-300" : "bg-gray-50 text-gray-600"}`}>Item</th>
                <th className={`py-1 px-3 ${darkMode ? "bg-gray-700 text-slate-300" : "bg-gray-50 text-gray-600"}`}>Quantity</th>
                <th className={`py-1 px-3 ${darkMode ? "bg-gray-700 text-slate-300" : "bg-gray-50 text-gray-600"}`}>Price</th>
                <th className={`py-1 px-3 ${darkMode ? "bg-gray-700 text-slate-300" : "bg-gray-50 text-gray-600"}`}>Cancel</th>
              </tr>
            </thead>
            <tbody className={`divide-y text-sm ${darkMode ? "bg-gray-800 divide-gray-700" : "bg-white divide-gray-200"}`}>
              {orderItems?.map((item) => (
                <tr key={item.cartKey || item.itemId}>
                  <td className="p-2 max-w-[160px]">
                    <div className={`truncate cursor-pointer font-bold ${darkMode ? "text-slate-100" : "text-gray-800"}`} title={item.name}>
                      {item.name}
                    </div>
                    {/* Variant badge */}
                    {item.variant && (
                      <span className="inline-block mt-0.5 text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                        {item.variant}
                      </span>
                    )}
                    {/* Add-on pills */}
                    {item.addOns && item.addOns.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.addOns.map((a, i) => (
                          <span key={i} className="text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-150 px-1.5 py-0.5 rounded-md whitespace-nowrap">
                            +{a.name} (₹{a.price})
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Per-item kitchen note */}
                    <input
                      type="text"
                      placeholder="Kitchen note..."
                      value={item.note || ""}
                      onChange={e => updateItemNote && updateItemNote(item.cartKey || item.itemId, e.target.value)}
                      className="mt-1.5 w-full text-[10px] bg-slate-50 border border-dashed border-slate-300 rounded-md px-2 py-1 text-slate-600 placeholder-slate-300 focus:outline-none focus:border-amber-400 focus:bg-amber-50/40 transition-colors"
                    />
                  </td>

                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => addToOrderdecrease(item)}
                        className="bg-red-500 hover:bg-red-600 
                 text-white w-7 h-7 rounded-full 
                 flex items-center justify-center transition"
                      >
                        <Minus size={16} />
                      </button>

                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => {
                          const value = Math.max(
                            1,
                            Number(e.target.value) || 1,
                          );
                          updateItemQty(item.cartKey || item.itemId, value);
                        }}
                        className="w-8 h-8 text-center rounded outline-none focus:ring-2 focus:ring-teal-500 appearance-none"
                      />

                      <button
                        onClick={() => addToOrderincrease(item)}
                        className="bg-green-500 hover:bg-green-600 
                 text-white w-7 h-7 rounded-full 
                 flex items-center justify-center transition"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </td>

                  <td className="py-2 px-3 text-nowrap text-center font-medium">
                    ₹ {formatIndianCurrency(item.price * item.qty)}
                  </td>

                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={() => removeFromOrder(item)}
                      className="bg-red-400 text-white hover:bg-red-500 w-8 h-8 rounded-full flex items-center justify-center transition"
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="discount flex flex-col gap-4 mt-4 print:hidden border-t pt-3 border-gray-100">
            {/* Happy Hours badge */}
            {happyHour && (
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <span className="text-amber-500 text-base leading-none">🎉</span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-amber-700 leading-none">{happyHour.name}</p>
                  <p className="text-[9px] text-amber-500 leading-none mt-0.5">{happyHour.startTime}–{happyHour.endTime} · -{happyHour.discountPercentage}% auto-applied</p>
                </div>
              </div>
            )}
            {/* Discount Group */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 shrink-0">
                <label className="font-semibold pl-1 text-sm text-gray-700">Discount:</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDiscountType("percent")}
                    className={`px-2 py-0.5 text-xs font-bold transition-colors ${discountType === "percent" ? "bg-teal-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  >%</button>
                  <button
                    type="button"
                    onClick={() => setDiscountType("fixed")}
                    className={`px-2 py-0.5 text-xs font-bold transition-colors ${discountType === "fixed" ? "bg-teal-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  >₹</button>
                </div>
              </div>
              {discountType === "percent" ? (
                <input
                  type="number"
                  className="border border-gray-300 rounded px-2 py-1 max-w-[90px] text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                  value={discount || ""}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  placeholder="0%"
                  min="0"
                  max="100"
                />
              ) : (
                <input
                  type="number"
                  className="border border-gray-300 rounded px-2 py-1 max-w-[90px] text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                  value={fixedDiscountAmount || ""}
                  onChange={(e) => setFixedDiscountAmount(parseFloat(e.target.value) || 0)}
                  placeholder="₹0"
                  min="0"
                />
              )}
            </div>

            {/* GST Group */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold pl-1 text-sm text-gray-700">GST (%)</span>
                <span className="text-xs text-gray-500 font-bold bg-gray-100 px-2 py-0.5 rounded">
                  Current: {gst}%
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {[0, 5, 12, 18].map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setGST(rate)}
                    className={`flex-1 min-w-[45px] py-1 text-xs font-bold rounded transition border ${
                      gst === rate
                        ? "bg-teal-600 border-teal-600 text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {rate}%
                  </button>
                ))}

                {/* Custom GST Input */}
                <div className="relative flex items-center max-w-[80px]">
                  <input
                    type="number"
                    placeholder="Custom"
                    className={`w-full border rounded pl-2 pr-5 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                      ![0, 5, 12, 18].includes(gst)
                        ? "border-teal-600 bg-teal-50 text-teal-800 font-bold"
                        : "border-gray-300"
                    }`}
                    value={![0, 5, 12, 18].includes(gst) ? gst : ""}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setGST(isNaN(val) ? 0 : val);
                    }}
                    min="0"
                    max="100"
                  />
                  <span className="absolute right-1.5 text-[10px] font-bold text-gray-400 pointer-events-none">%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="my-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Choose Payment Method:</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "COD",   label: "Cash" },
                { id: "UPI",   label: "UPI" },
                { id: "CARD",  label: "Card" },
                { id: "SPLIT", label: "Cash + UPI" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setPaymentMethod(id); setSplitCash(""); }}
                  className={`py-2 px-3 text-xs font-bold rounded-xl border transition-all duration-150 ${
                    paymentMethod === id
                      ? "bg-teal-600 border-teal-600 text-white shadow-md shadow-teal-600/10"
                      : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {paymentMethod === "SPLIT" && (() => {
              const total = Math.round(calculateFinalAmount());
              const cashAmt = parseFloat(splitCash) || 0;
              const upiAmt = Math.max(0, total - cashAmt);
              return (
                <div className="mt-3 p-3 rounded-xl border border-teal-200 bg-teal-50/50 space-y-2">
                  <p className="text-[10px] font-black uppercase tracking-wider text-teal-700">Split breakdown — Total: ₹{total}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 w-14 shrink-0">Cash ₹</span>
                    <input
                      type="number"
                      min="0"
                      max={total}
                      value={splitCash}
                      onChange={e => setSplitCash(e.target.value)}
                      placeholder="0"
                      className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-600 w-14 shrink-0">UPI ₹</span>
                    <div className="flex-1 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-right font-semibold text-teal-700">
                      {upiAmt.toFixed(2)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {paymentMethod === "UPI" && upiDetails && (
            <div className="flex justify-center items-center my-4">
              <QrCode
                upiDetails={upiDetails}
                totalAmount={calculateFinalAmount().toFixed(2)}
              />
            </div>
          )}

          {/* 🔥 Loyalty & Wallet Redemption */}
          {isExistingCustomer && customerProfile && (
            (merchantSettings?.loyaltySettings?.enabled && (customerProfile.loyaltyPoints || 0) >= (merchantSettings?.loyaltySettings?.minRedeemPoints || 10)) ||
            (merchantSettings?.walletSettings?.enabled && (customerProfile.walletBalance || 0) > 0)
          ) && (
            <div className="mt-3 space-y-2.5 border border-indigo-100 bg-indigo-50/50 rounded-xl p-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-indigo-700 mb-1">💎 Rewards & Wallet</p>

              {merchantSettings?.loyaltySettings?.enabled &&
                (customerProfile.loyaltyPoints || 0) >= (merchantSettings?.loyaltySettings?.minRedeemPoints || 10) && (
                <label className="flex items-center gap-2 cursor-pointer text-xs select-none">
                  <input
                    type="checkbox"
                    checked={redeemPoints}
                    onChange={(e) => setRedeemPoints(e.target.checked)}
                    className="w-4 h-4 accent-indigo-600 rounded"
                  />
                  <span className="text-slate-700 font-medium">
                    Redeem <strong className="text-indigo-700 font-bold">{customerProfile.loyaltyPoints} pts</strong>
                    {" "}
                    (~₹{((customerProfile.loyaltyPoints || 0) * (merchantSettings?.loyaltySettings?.redemptionRate || 1)).toFixed(0)} off)
                  </span>
                </label>
              )}

              {merchantSettings?.walletSettings?.enabled &&
                (customerProfile.walletBalance || 0) > 0 && (
                <label className="flex items-center gap-2 cursor-pointer text-xs select-none">
                  <input
                    type="checkbox"
                    checked={useWallet}
                    onChange={(e) => setUseWallet(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600 rounded"
                  />
                  <span className="text-slate-700 font-medium">
                    Pay from Wallet <strong className="text-emerald-700 font-bold">₹{(customerProfile.walletBalance || 0).toFixed(2)}</strong>
                  </span>
                </label>
              )}
            </div>
          )}

          <div className="flex flex-col mt-5 pt-3 border-t border-slate-100 gap-2">
            {merchantSettings?.platformFee?.enabled && merchantSettings.platformFee.percentage > 0 && (
              <div className="flex flex-row justify-between items-center">
                <p className="text-xs font-semibold text-slate-500">Processing Fee ({merchantSettings.platformFee.percentage}%)</p>
                <p className="text-sm font-semibold text-orange-600">
                  + ₹ {formatIndianCurrency(calcBreakdown().platformFeeAmount)}
                </p>
              </div>
            )}
            <div className="flex flex-row justify-between items-center">
              <p className="text-sm font-bold text-slate-800">Total Amount</p>
              <p className="text-xl font-black text-teal-600">
                ₹ {formatIndianCurrency(calculateFinalAmount())}
              </p>
            </div>
          </div>
        </div>

<div className="flex flex-row gap-2 mt-4">
  {/* Cancel Order Button (hidden for roles that may not cancel orders) */}
  {onCancelOrder && (
  <button
    onClick={onCancelOrder}
    disabled={billingLoading}
    className="px-3.5 py-2.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-xl transition duration-150 flex items-center justify-center shrink-0 disabled:opacity-50"
    title="Cancel Order"
  >
    Cancel
  </button>
  )}

  {/* Print KOT Button — only for ongoing dine-in/table orders */}
  {orderId && onPrintKOT && (
    <button
      onClick={onPrintKOT}
      disabled={billingLoading || !orderItems || orderItems.length === 0}
      title="Print Kitchen Order Ticket"
      className="px-3 py-2.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition duration-150 flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-40"
    >
      <Printer className="w-3.5 h-3.5" /> KOT
    </button>
  )}

  {/* Generate Bill Button */}
  <button
    className="flex-grow py-2.5 px-4 text-xs md:text-sm font-bold bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-md shadow-teal-600/10 transition duration-150 flex items-center justify-center gap-2 disabled:opacity-50"
    onClick={() => {
      const total = Math.round(calculateFinalAmount());
      const cashAmt = Math.min(parseFloat(splitCash) || 0, total);
      const upiAmt = Math.max(0, total - cashAmt);
      const splits = paymentMethod === "SPLIT"
        ? [{ method: "CASH", amount: cashAmt }, { method: "UPI", amount: upiAmt }]
        : [];
      generateBillSlip(paymentMethod, { redeemPoints, useWallet, splitPayments: splits });
    }}
    disabled={billingLoading}
  >
    {billingLoading ? (
      <>
        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
        Generating...
      </>
    ) : (
      "Generate Bill"
    )}
  </button>
</div>
      </div>
    </>
  );
};

export default BillingDetails;
